import React from "react";

// linkify — render a plain-text string so any URL becomes a clickable anchor
// that opens in a NEW TAB. Used by the conversation/message surfaces so links
// inside message bodies (e.g. ClickUp task-comment notifications like
// `[https://app.clickup.com/t/...](https://app.clickup.com/t/...)` or bare
// `https://app.clickup.com/t/...`) are clickable, not dead text.
//
// Safe by construction: no dangerouslySetInnerHTML. Output is React nodes.
//
// Two link forms are recognised:
//   1. Markdown:  [label](https://example.com/x)   → label is the link text
//   2. Bare URL:  https://example.com/x            → the URL is the link text
//
// Trailing sentence punctuation / wrapping brackets on BARE urls are stripped
// off the href and re-emitted as plain text afterwards, so a URL wrapped in
// parentheses — "(https://app.clickup.com/t/868kq3paz)" — links the URL only
// and keeps the ")" as text. Markdown hrefs are already bounded by ")"

const LINK_RE =
  /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<]+)/g;

const LINK_CLASS = "text-[#32B1FF] hover:underline break-all";

// Strip trailing punctuation / wrapping brackets that are not part of the URL.
// A closing bracket is only removed when its opener is NOT present in the URL
// (so a URL that genuinely contains "(...)" is preserved).
function trimTrailing(url) {
  let u = url;
  let changed = true;
  while (changed) {
    changed = false;
    if (/[.,;:!?]$/.test(u)) {
      u = u.slice(0, -1);
      changed = true;
      continue;
    }
    if (/['"’”]$/.test(u)) {
      u = u.slice(0, -1);
      changed = true;
      continue;
    }
    if (u.endsWith(")") && !u.includes("(")) {
      u = u.slice(0, -1);
      changed = true;
      continue;
    }
    if (u.endsWith("]") && !u.includes("[")) {
      u = u.slice(0, -1);
      changed = true;
      continue;
    }
    if (u.endsWith("}") && !u.includes("{")) {
      u = u.slice(0, -1);
      changed = true;
    }
  }
  return u;
}

/**
 * Tokenise `text` into an array of React nodes where URLs are anchors.
 * Returns `[""]` for empty/non-string input so callers can always render the
 * result directly. A body with no URLs collapses to a single string node, so
 * existing `getByText(body)` tests on URL-free bodies keep working.
 *
 * @param {string} text
 * @returns {Array<React.ReactNode>}
 */
export function linkify(text) {
  if (!text || typeof text !== "string") return [""];
  const nodes = [];
  let last = 0;
  let key = 0;
  let m;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    const matchStart = m.index;
    if (matchStart > last) nodes.push(text.slice(last, matchStart));

    if (m[1] !== undefined && m[2] !== undefined) {
      // Markdown [label](url)
      nodes.push(
        <a key={`lnk-${key++}`} href={m[2]} target="_blank" rel="noreferrer" className={LINK_CLASS}>
          {m[1]}
        </a>,
      );
      last = matchStart + m[0].length;
    } else {
      // Bare url — trim trailing punctuation, keep the trimmed tail as text.
      const raw = m[3];
      const href = trimTrailing(raw);
      nodes.push(
        <a key={`lnk-${key++}`} href={href} target="_blank" rel="noreferrer" className={LINK_CLASS}>
          {href}
        </a>,
      );
      const tail = raw.slice(href.length);
      if (tail) nodes.push(tail);
      last = matchStart + m[0].length;
    }
  }
  if (last < text.length) nodes.push(text.slice(last));
  if (nodes.length === 0) return [text];
  return nodes;
}

/**
 * Convenience component: <LinkifyText text="..."/> renders the linkified text
 * inside a <span>. For elements that need the linkified nodes as direct
 * children (to preserve a parent's className, e.g. a <p>), call linkify()
 * directly instead of using this wrapper.
 */
export default function LinkifyText({ text, className = "" }) {
  return <span className={className}>{linkify(text)}</span>;
}
